/**
 * Image utility functions for Gallery Builder
 */

/**
 * Widths to preload for variant image swaps.
 * Ordered to prioritize mobile display size first.
 */
export const VARIANT_PRELOAD_WIDTHS = [536, 630, 872, 150];

/**
 * Get optimized image URL with specific width
 * @param {string} imageUrl - Original image URL
 * @param {number} width - Desired width in pixels
 * @returns {string} Image URL with width parameter
 */
export function getImageUrl(imageUrl, width) {
  if (!imageUrl) return '';
  const baseUrl = imageUrl.split('?')[0];
  return `${baseUrl}?width=${width}`;
}

/**
 * Get responsive srcset for an image
 * @param {string} imageUrl - Original image URL
 * @param {Array<number>} sizes - Array of widths for srcset
 * @returns {string} Srcset string for responsive images
 */
export function getImageSrcSet(imageUrl, sizes = [150, 300, 600, 900, 1200]) {
  if (!imageUrl) return '';
  const baseUrl = imageUrl.split('?')[0];
  return sizes.map(size => `${baseUrl}?width=${size} ${size}w`).join(', ');
}

/**
 * Get thumbnail URL for gallery carousel
 * @param {string} imageUrl - Original image URL
 * @returns {string} Thumbnail URL
 */
export function getThumbnailUrl(imageUrl) {
  return getImageUrl(imageUrl, 150);
}

/**
 * Get gallery display URL
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Size preset ('small', 'medium', 'large')
 * @returns {string} Display URL
 */
export function getGalleryDisplayUrl(imageUrl, size = 'medium') {
  const widths = {
    small: 630,
    medium: 872,
    large: 1200
  };
  return getImageUrl(imageUrl, widths[size] || widths.medium);
}

/**
 * Preload a single image
 * @param {string} url - Image URL to preload
 * @returns {Promise<boolean>} Promise that resolves when loaded
 */
export function preloadImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();

    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);

    img.src = url;
  });
}

/**
 * Preload multiple images in parallel
 * @param {Array<string>} urls - Array of image URLs
 * @returns {Promise<Array<boolean>>} Array of load results
 */
export async function preloadImages(urls) {
  if (!urls || urls.length === 0) return [];

  return Promise.all(urls.map(url => preloadImage(url)));
}

/**
 * Get variant image URLs for preloading
 * @param {Object} variant - Variant object
 * @param {Array<number>} sizes - Sizes to preload
 * @returns {Array<string>} Array of URLs to preload
 */
export function getVariantImageUrls(variant, sizes = VARIANT_PRELOAD_WIDTHS) {
  if (!variant?.featured_image?.src) return [];

  const baseUrl = variant.featured_image.src.split('?')[0];
  return sizes.map(size => `${baseUrl}?width=${size}`);
}

/**
 * Get orientation from product image dimensions
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {string} 'horizontal' or 'vertical'
 */
export function getImageOrientation(width, height) {
  if (!width || !height) return 'horizontal';
  return width > height ? 'horizontal' : 'vertical';
}

/**
 * Get placeholder image URL for loading states
 * @param {string} orientation - 'horizontal' or 'vertical'
 * @returns {string} Placeholder image URL
 */
export function getPlaceholderUrl(orientation = 'horizontal') {
  const dimensions = orientation === 'horizontal' ? '600x400' : '400x600';
  return `data:image/svg+xml,%3Csvg width='${dimensions.split('x')[0]}' height='${dimensions.split('x')[1]}' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3C/svg%3E`;
}

/**
 * Check if image URL is valid
 * @param {string} url - Image URL
 * @returns {boolean} True if valid
 */
export function isValidImageUrl(url) {
  if (!url || typeof url !== 'string') return false;

  // Check for common image extensions or Shopify CDN patterns
  const validPatterns = [
    /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i,
    /cdn\.shopify\.com/i,
    /shopify-assets\.shopifycdn\.com/i
  ];

  return validPatterns.some(pattern => pattern.test(url));
}
